/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/unbound-method */
import UserPersonalInfoApi, { personalInfoCheck } from '@/api/userPersonalInfo';
import ButtonBar from '@/components/button-bar';

import type { UserVo } from '@/vo/user';
import type { SelectOptionDataType } from '@hb/finance-ui/src/types/shared';
import type { VNode } from 'vue';

import { shouldShowContactImportIcon } from '@/views_v2/verify-credit/show-contact-icon';
import { Debounce } from '@/shared/decorators/debounce';
import store from '@/store';
import {
  Field,
  Form,
  Formatter,
  ValidateError,
  Validator,
} from '@hb/finance-ui';
import { Toast } from '@hb/coffee';
import {
  Component,
  Mixins,
  Prop,
  Vue,
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { MaritalStatusEnum, UpdateZyqSceneEnum, UpdateZyqSourceEnum } from '@/api/userPersonalInfo/types';
import { nameMaskFormat, phoneNoMaskFormat } from '@/shared/utils/dataMaskFormat';
import { trimAll, trimValue } from '@/shared/utils/trim';
import { checkVirtualNumber, validatorPersonName } from '@/shared/utils/validator';
import { VerifySceneEnum } from '@/shared/verify-credit-controller/types';
import { CONTACT_SPOUSE_RELATION_CODE, VerifyEntireTypeEnum } from '@/views_v2/verify-credit/types';
import type { VerifyStageContext } from '@/views_v2/verify-credit/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { IS_PRERENDERING } from '@hb/coffee-prerender';
import { handleImportClick } from '@/shared/utils/contactImport/handleImport';
import { ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import { TP_CONTACT_IMPORT_SUCCESS } from '@/views_v2/verify-credit/tianqi';
import { ContactFormItems } from './store';
import VerifyBasicPersonal from '../../../mixins/verify-basic-personal';
import BasicPersonalHeader from '../../basic-personal-header';

import style from './index.module.scss';
import { InitFormParams, OriginContact } from './types';

const PersonalContactsStore = namespace('personalContacts');

// Field 类型扩展，包含运行时动态添加的 validateMessage 属性
type FieldComponent = Field & { validateMessage?: string };

// 当前需要校验的联系人名字敏感字符
const SentiveNameList = [
  '姥', '爷', '婆', '公', '奶',
  '爸', '妈', '叔', '姨', '舅',
  '哥', '兄', '姐', '弟', '妹',
  '丈夫', '老公', '老婆', '妻子', '媳妇',
  '父', '母', '伯', '婶', '姑',
  '婿', '嫂',
];

@Component({
  name: 'PersonalContacts',
})
export default class PersonalContacts extends Mixins(VerifyBasicPersonal) {
  /** 当前是否“最后一步” */
  @Prop(Boolean) readonly processIsLastVerify!: boolean;

  @Prop(String) readonly verifyScene!: VerifySceneEnum;

  @Prop(String) readonly verifyEntireType!: VerifyEntireTypeEnum;

  @Prop([String]) readonly commodityIds!: string[];

  @Prop(Object) readonly verifyStageContext!: VerifyStageContext;

  @Prop(Boolean) readonly isVerifyStageHeaderUINew!: boolean;

  @Prop(Boolean) readonly ocrAfterLoan!: boolean;

  @PersonalContactsStore.State formData!: UserVo.PersonalInfo | null;

  @PersonalContactsStore.State contactRelations!: SelectOptionDataType[];

  @PersonalContactsStore.State maritalStatus!: MaritalStatusEnum | null;

  @PersonalContactsStore.Action initFormData!: (params: InitFormParams) => Promise<void>;

  @PersonalContactsStore.Action initSelectOptions!: () => Promise<void>;

  @PersonalContactsStore.Action $updateState!: (data: Dictionary) => void;

  @PersonalContactsStore.State originContactList!: OriginContact[];

  // 是否显示联系人导入图标（从 store 读取）
  private get showImportIcon(): boolean {
    return !!(store.state.verifyCredit && store.state.verifyCredit.shouldShowContactImportIcon);
  }

  // 底部按钮文案
  private get confirmText() {
    return this.processIsLastVerify ? '提交' : '下一步';
  }

  private async created() {
    this.$ubtModuleExpose(
      'finance_editPlatform',
      'platform',
      this.platformValue,
    );
    await shouldShowContactImportIcon();
  }

  private canSelectContactRelations(contactIndex?: number) {
    const { lockNoSpouseInfo } = this.verifyStageContext;
    /**
     * 不可选择配偶：
     * 1. 自营晋商-未婚
     * 2. 自身选项不为配偶且其他联系人已选配偶
     * */
    if (lockNoSpouseInfo
      || (typeof contactIndex === 'number'
        && (
          this.formData?.userEmergencyContacts?.[contactIndex]?.contactRelation
          !== CONTACT_SPOUSE_RELATION_CODE
          && this.formData?.userEmergencyContacts?.some(
            (item, index) => index !== contactIndex
            && item.contactRelation === CONTACT_SPOUSE_RELATION_CODE
          )
        )
      )
    ) {
      return this.contactRelations.filter((item) => Number(item.value || '') !== CONTACT_SPOUSE_RELATION_CODE);
    }

    return this.contactRelations;
  }

  private async setLockSpouseInfo() {
    if (this.maritalStatus) {
      const { lockEmergency, lockUnMarried } = await personalInfoCheck(
        this.commodityIds[0],
        this.maritalStatus,
      );

      this.$emit('updateVerifyStageContext', {
        lockSpouseInfo: !!lockEmergency,
        lockNoSpouseInfo: !!lockUnMarried,
      });
    }
  }

  async mounted(): Promise<void> {
    if (!IS_PRERENDERING) {
      const { requireSpouseInfo } = this.verifyStageContext;

      await this.initSelectOptions();
      await this.initFormData({ requireSpouseInfo });
      await this.setLockSpouseInfo();

      this.checkFormReady();
      this.$emit('ubtExposeModule');
      /** 页面初始化后，“手机号”与“关系”表单触发正确性检验 */
      this.initValidate();
    }
  }

  private clearAllFieldsError(): void {
    Vue.nextTick(() => {
      ContactFormItems.forEach((_, idx) => {
        try {
          // 清除姓名字段的错误（validateMessage 是运行时动态添加的属性）
          const nameField = this.$refs[`contactName${idx}`] as FieldComponent;
          if (nameField) {
            nameField.validateMessage = '';
          }

          // 清除手机号字段的错误
          const phoneField = this.$refs[`contactPhone${idx}`] as FieldComponent;
          if (phoneField) {
            phoneField.validateMessage = '';
          }

          // 清除关系字段的错误
          const relationField = this.$refs[`contactRelation${idx}`] as FieldComponent;
          if (relationField) {
            relationField.validateMessage = '';
          }
        } catch (err) {
          ubtSendError(err as Error);
        }
      });
    });
  }

  private getOriginContact(idx: number): OriginContact {
    return this.originContactList[idx] || {};
  }

  private getShouldMaskContactName(idx: number) {
    return (this.originContactList[idx] || {}).shouldMaskContactName;
  }

  private getShouldMaskContactPhone(idx: number) {
    return (this.originContactList[idx] || {}).shouldMaskContactPhone;
  }

  // 姓名校验
  private validatorName(idx: number, value: string): boolean {
    return this.getShouldMaskContactName(idx) || validatorPersonName(value);
  }

  // 姓名格式化
  private nameFor(idx: number, value: string): string {
    return this.getShouldMaskContactName(idx)
      ? nameMaskFormat(value) : trimAll(value);
  }

  // 电话校验
  private validatorPhoneNo(idx: number, value: string): boolean {
    return this.getShouldMaskContactPhone(idx) || Validator.isPhoneNo(value);
  }

  // 联系人手机号是否是虚拟号校验判断
  private validatorVirtualNumber(idx: number, value: string): boolean {
    if (Validator.isPhoneNo(value)) {
      return this.getShouldMaskContactPhone(idx) || checkVirtualNumber(value);
    }
    return true;
  }

  // 联系人手机号码不能和注册号码一致
  private async checkUserContactPhoneResult(idx: number, value: string): Promise<boolean> {
    const trimPhoneNum = trimValue(value);
    // 判断是否手机号
    if (Validator.isPhoneNo(trimPhoneNum)) {
      const { checkPhoneNumRes } = await UserPersonalInfoApi
        .checkUserContact({ contactPhone: trimPhoneNum }) || {};

      return this.getShouldMaskContactPhone(idx) || !checkPhoneNumRes;
    }
    return true;
  }

  /**
   * 联系人手机号码与银行卡预留手机不可一致检验
   * 若是已入库的手机号(服务端返回)，默认进行检验(相比历史逻辑，不同的点)
   */
  private async checkContactPhoneSameWithBankCard(idx: number, value: string): Promise<boolean> {
    const phoneNum = this.getShouldMaskContactPhone(idx)
      ? this.getOriginContact(idx).defalutContactPhone : trimValue(value);

    /**
     * 若是非手机号，则返回检验失败
     * 若是手机号，则请求服务-检验是否与银行卡预留手机号一致?
     */
    if (Validator.isPhoneNo(phoneNum)) {
      const { isSamePhoneNumRes } = await UserPersonalInfoApi
        .checkUserContact({ contactPhone: phoneNum }) || {};

      return !isSamePhoneNumRes;
    }

    return true;
  }

  // 联系人姓名不能和注册姓名一致
  private async checkUserContactNameResult(idx: number, value: string): Promise<boolean> {
    const trimName = trimValue(value);
    // 判断是否中文姓名
    if (validatorPersonName(trimName)) {
      const { checkNameRes } = await UserPersonalInfoApi
        .checkUserContact({ contactName: trimName }) || {};

      return this.getShouldMaskContactName(idx) || !checkNameRes;
    }

    return true;
  }

  // 联系人姓名不能包含敏感字符
  private checkSensitiveContactName(idx: number, value: string): boolean | Error {
    const trimName = trimValue(value);
    // 判断是否中文姓名
    if (validatorPersonName(trimName)) {
      const checkNameRes = SentiveNameList.find(
        (item) => trimName.indexOf(item) > -1,
      );

      return this.getShouldMaskContactName(idx) || !checkNameRes || new Error(`联系人姓名不能包含【${checkNameRes}】等称谓词`);
    }
    return true;
  }

  // 电话格式化
  private phoneNoFor(idx: number, value: string): string {
    return this.getShouldMaskContactPhone(idx)
      ? phoneNoMaskFormat(value) : Formatter.phoneNoFormatter(value);
  }

  // 配偶仅可填写1位
  private validatorOnlyOneSpouse(): boolean {
    if (this.formData
      && this.formData.userEmergencyContacts.filter(
        (item) => item.contactRelation === CONTACT_SPOUSE_RELATION_CODE,
      ).length > 1
    ) {
      return false;
    }
    return true;
  }

  /** 仅在哈啰app内且不包括鸿蒙系统，以及版本为6.98.70及以上，才允许导入联系人 */
  private async onImportClick(contactIndex: number) {
    const importedContactInfo = await handleImportClick();
    if (importedContactInfo) {
      // 导入成功后，更新姓名、手机号
      this.onEmergencyFieldChange(contactIndex, 'contactName', importedContactInfo.name, undefined, true);
      this.onEmergencyFieldChange(contactIndex, 'contactPhone', importedContactInfo.phone, undefined, true);
      ubtSetupModuleExpose(TP_CONTACT_IMPORT_SUCCESS);
      this.initValidateAfterImport();
    }
  }

  private render() {
    return (
      <div class={[style.container]}>
        <BasicPersonalHeader
          isVerifyStageHeaderUINew={this.isVerifyStageHeaderUINew}
          verifyScene={this.verifyScene}
          isLoanProcess={this.verifyEntireType === VerifyEntireTypeEnum.LOAN}
          ocrAfterLoan={this.ocrAfterLoan}
        />
        <Form
          class={style.formBox}
          ref="form"
          style={{ backgroundColor: 'transparent' }}
          onSubmit={this.onSubmit}>
          {ContactFormItems.map(
            (title, contactIndex) => this.renderSection(title, [
              <Field
                label="姓名"
                placeholder="请填写联系人的姓名"
                class="verifyCreditField"
                maxLength={25}
                formatter={this.nameFor.bind(this, contactIndex)}
                {...this.getEmergencyRenderProps(
                  contactIndex,
                  'contactName',
                  '臻有钱_个人信息_修改姓名',
                )}
                rules={[
                  { required: true, message: '请填写联系人的姓名' },

                  {
                    validator: this.checkUserContactNameResult.bind(this, contactIndex),
                    message: '请勿输入本人姓名',
                    trigger: 'onBlur',
                  },
                  {
                    validator: this.contactRepeatValidatorWithMask.bind(
                      this,
                      contactIndex,
                      'contactName',
                    ),
                    message: '联系人姓名不能够重复',
                    trigger: 'onBlur',
                  },
                  {
                    validator: this.validatorName.bind(this, contactIndex),
                    message: '联系人姓名只能包含2～25个汉字',
                    trigger: 'onBlur',
                  },
                  {
                    validator: this.checkSensitiveContactName.bind(this, contactIndex),
                    message: '请正确填写联系人姓名',
                    trigger: 'onBlur',
                  },
                ]}
              >
                {this.showImportIcon && (
                  <div slot="input-right">
                    <div
                      class={style.contactNameIcon}
                      onClick={this.onImportClick.bind(this, contactIndex)}
                    />
                  </div>
                )}
              </Field>,

              <Field
                type="tel"
                label="手机号"
                placeholder="请填写联系人的手机号"
                class="verifyCreditField"
                maxLength={13}
                formatter={this.phoneNoFor.bind(this, contactIndex)}
                {...this.getEmergencyRenderProps(
                  contactIndex,
                  'contactPhone',
                  '臻有钱_个人信息_修改电话号码',
                )}
                rules={[
                  { required: true, message: '请填写联系人的手机号' },
                  {
                    validator: this.checkUserContactPhoneResult.bind(this, contactIndex),
                    message: '请勿输入本人手机号',
                    trigger: 'onBlur',
                  },
                  {
                    validator: this.checkContactPhoneSameWithBankCard.bind(this, contactIndex),
                    message: '请勿填写本人银行卡预留手机号',
                    trigger: 'onBlur',
                  },
                  {
                    validator: this.validatorVirtualNumber.bind(this, contactIndex),
                    message: '该号段不支持，请输入正确的号码',
                    trigger: 'onBlur',
                  },
                  {
                    validator: this.contactRepeatValidatorWithMask.bind(
                      this,
                      contactIndex,
                      'contactPhone',
                    ),
                    message: '联系人手机号不能够重复',
                    trigger: 'onBlur',
                  },
                  {
                    validator: this.validatorPhoneNo.bind(this, contactIndex),
                    message: '请填写正确的手机号',
                    trigger: 'onBlur',
                  },
                ]}
              />,
              <Field
                name={`contactRelation${contactIndex}`}
                label="关系"
                placeholder="您与联系人的关系是？"
                class="verifyCreditField"
                options={this.canSelectContactRelations(contactIndex)}
                {...this.getEmergencyRenderProps(
                  contactIndex,
                  'contactRelation',
                  'finance_BaseInfoProfile_SelectContactRelations',
                )}
                rules={[
                  { required: true, message: '请选择您与联系人的关系' },
                  {
                    validator: this.validatorOnlyOneSpouse.bind(this),
                    message: '配偶仅可填写1位',
                    trigger: 'onBlur',
                  },
                ]}
              />,
            ]),
          )}
        </Form>
        <div class={style.buttonBox}>
          <ButtonBar
            style={this.submitBtnInTransparent ? { backgroundColor: 'transparent', boxShadow: 'none' } : null}
            confirmText={this.confirmText}
            onConfirm={this.onSubmitButtonClick}
          />
        </div>
      </div>
    );
  }

  private renderSection(title: string, fields: VNode[]): VNode {
    return (
      <div class={[style.fieldSection]}>
        <div class={style.fieldSectionHeader}>
          <div class={style.fieldSectionTitle}>{title}</div>
        </div>
        <div class={style.fieldSectionContent}>
          {fields}
        </div>
      </div>
    );
  }

  // 只要验证紧急联系人姓名或者电话是否重复
  private needCheckEmergencyRepeation = {
    contactName: true,
    contactPhone: true,
  };

  // 脱敏的时候 不校验
  private contactRepeatValidatorWithMask(
    idx: number,
    filedName: 'contactName' | 'contactPhone',
    fieldValue: string,
  ): boolean {
    switch (filedName) {
      case 'contactName':
        return this.getShouldMaskContactName(idx)
          || this.contactRepeatValidator(idx, filedName, fieldValue);
      case 'contactPhone':
        return this.getShouldMaskContactPhone(idx)
          || this.contactRepeatValidator(idx, filedName, fieldValue);
      default:
        return true;
    }
  }

  private contactRepeatValidator(
    contactIndex: number,
    filedName: 'contactName' | 'contactPhone',
    fieldValue: string,
  ) {
    if (this.formData && fieldValue) {
      const { userEmergencyContacts = [] } = this.formData;
      const compareIndex = Math.abs(contactIndex - 1);
      const compareTarget = userEmergencyContacts[compareIndex];
      const $compareRef = this.$refs[`${filedName}${compareIndex}`] as Field;
      const compareOriginContact = this.getOriginContact(compareIndex);
      let compareValue = '';

      if (filedName === 'contactName' && compareTarget) {
        compareValue = compareOriginContact.shouldMaskContactName
          ? compareOriginContact.defalutContactName : trimValue(compareTarget[filedName]);
      } else if (filedName === 'contactPhone' && compareTarget) {
        compareValue = compareOriginContact.shouldMaskContactPhone
          ? compareOriginContact.defalutContactPhone : trimValue(compareTarget[filedName]);
      }

      if (compareValue) {
        // 当前数据验证通过后，对比目标属性重新验证，避免修改了一个之后，另外一个依然提示**重复
        if ($compareRef && this.needCheckEmergencyRepeation[filedName]) {
          this.needCheckEmergencyRepeation[filedName] = false;
          $compareRef.validate();
        } else {
          this.needCheckEmergencyRepeation[filedName] = true;
        }
        // 联系人姓名或手机号重复
        return compareValue !== trimValue(fieldValue);
      }

      this.needCheckEmergencyRepeation[filedName] = true;
    }

    return true;
  }

  private getEmergencyPropValue(
    contactIndex: number,
    propName: keyof UserVo.ContactInfo,
  ): string | number {
    if (this.formData) {
      const { userEmergencyContacts } = this.formData;

      if (userEmergencyContacts && userEmergencyContacts[contactIndex]) {
        return userEmergencyContacts[contactIndex][propName] || '';
      }
    }

    return '';
  }

  private getEmergencyRenderProps(
    contactIndex: number,
    fieldName: keyof UserVo.ContactInfo,
    buttonName?: string,
  ): Dictionary {
    const isSelectComp = fieldName === 'contactRelation';
    let fieldValue = this.getEmergencyPropValue(contactIndex, fieldName);
    const { requireSpouseInfo, lockSpouseInfo } = this.verifyStageContext;
    const { userEmergencyContacts } = this.formData || {};
    /** 是否禁止用户选择，仅在“关系”可能会设置true */
    let isDisabled = false;

    /** 若是锁配偶，则关系强制填写“配偶”关系 */
    if (this.formData && fieldName === 'contactRelation' && (requireSpouseInfo || lockSpouseInfo)) {
      if (
        userEmergencyContacts?.length === 2
        && userEmergencyContacts.every((it) => it.contactRelation)) {
        /** 已是配偶关系，禁止用户选择  */
        isDisabled = Number(fieldValue || '') === CONTACT_SPOUSE_RELATION_CODE;
      } else if (contactIndex === 0) {
        /** 若是未填联系人、只填了1个联系人(极端case)，则简单处理(不考虑已填联系人的关系)，直接锁定第一个联系人为配偶 */
        isDisabled = true;
        this.formData.userEmergencyContacts[0].contactRelation = CONTACT_SPOUSE_RELATION_CODE;
        fieldValue = CONTACT_SPOUSE_RELATION_CODE;
      }
    }

    return {
      ref: `${fieldName}${contactIndex}`,
      attrs: {
        name: `${fieldName}${contactIndex}`,
        value: fieldValue,
        disabled: isDisabled,
      },
      on: {
        [isSelectComp ? 'change' : 'input']: (val: string) => this.onEmergencyFieldChange(
          contactIndex,
          fieldName,
          val,
          buttonName,
        ),
      },
    };
  }

  /**
   * @param isImport 是否为导入操作，默认值为fasle
   */
  private onEmergencyFieldChange(
    idx: number,
    fieldName: keyof UserVo.ContactInfo,
    fieldValue: string,
    buttonName?: string,
    isImport = false,
  ): void {
    const { userEmergencyContacts = [] } = this.formData || {};
    if (buttonName) {
      this.$ubtPageButton(
        buttonName,
        fieldName,
        fieldValue,
        'contactIndex',
        String(idx),
      );
    }

    let fieldNameValue = fieldValue;

    // 导入操作：直接取消脱敏
    // 手动输入：只有当长度不等于原始长度时才取消脱敏
    if (fieldName === 'contactName') {
      if (this.getShouldMaskContactName(idx)) {
        // eslint-disable-next-line max-len
        const originContactNameLength = fieldValue.length !== this.getOriginContact(idx).defalutContactName.length;
        if (isImport || originContactNameLength) {
          if (!isImport) {
            fieldNameValue = '';
          }

          this.originContactList[idx].shouldMaskContactName = false;
          this.$updateState({
            shouldMaskContactName: false,
          });
        }
      }
    }
    if (fieldName === 'contactPhone') {
      if (this.getShouldMaskContactPhone(idx) && fieldValue.length < 13) {
        if (!isImport) {
          fieldNameValue = '';
        }
        this.originContactList[idx].shouldMaskContactPhone = false;

        this.$updateState({
          originContactList: this.originContactList,
        });
      }
    }
    /**
     * 当前联系人关系发生变化时，触发另外一个联系人关系校验
     * 场景：当两个联系人关系都为配偶时，修改其中一个联系人关系，另外一个联系人关系需要重新校验
     */
    if (fieldName === 'contactRelation') {
      ContactFormItems.forEach((_, index) => {
        if (index !== idx) {
          const $contactRelation = this.$refs[`contactRelation${index}`] as Field;
          if ($contactRelation && $contactRelation.getValue && $contactRelation.getValue() && typeof $contactRelation.validate === 'function') {
            $contactRelation.validate();
          }
        }
      });
    }

    userEmergencyContacts[idx] = {
      ...(userEmergencyContacts[idx] || {}),
      contactNo: idx,
      [fieldName]: fieldNameValue,
    };

    this.$updateState({
      formData: {
        ...this.formData,
        userEmergencyContacts,
      },
    });
  }

  private initValidate() {
    /** 在Vue.nextTick后触发"手机号"与"关系"的检验，保证此时表单dom渲染更新已完成 */
    Vue.nextTick(() => {
      ContactFormItems.forEach((_, idx) => {
        try {
          // 校验手机号
          const $contactPhone = this.$refs[`contactPhone${idx}`] as Field;
          if ($contactPhone && $contactPhone.getValue && $contactPhone.getValue() && typeof $contactPhone.validate === 'function') {
            $contactPhone.validate();
          }

          // 校验关系
          const $contactRelation = this.$refs[`contactRelation${idx}`] as Field;
          if ($contactRelation && $contactRelation.getValue && $contactRelation.getValue() && typeof $contactRelation.validate === 'function') {
            $contactRelation.validate();
          }
          // 清除所有字段的错误状态
          this.clearAllFieldsError();
        } catch (err) {
          ubtSendError(err as Error);
        }
      });
    });
  }

  private initValidateAfterImport() {
    /** 导入联系人后，在Vue.nextTick后触发"手机号"与"姓名"的检验，保证此时表单dom渲染更新已完成 */
    Vue.nextTick(() => {
      ContactFormItems.forEach((_, idx) => {
        try {
          // 校验手机号
          const $contactPhone = this.$refs[`contactPhone${idx}`] as Field;
          if ($contactPhone && $contactPhone.getValue && $contactPhone.getValue() && typeof $contactPhone.validate === 'function') {
            $contactPhone.validate();
          }

          // 校验关系
          const $contactRelation = this.$refs[`contactName${idx}`] as Field;
          if ($contactRelation && $contactRelation.getValue && $contactRelation.getValue() && typeof $contactRelation.validate === 'function') {
            $contactRelation.validate();
          }
        } catch (err) {
          ubtSendError(err as Error);
        }
      });
    });
  }

  @Debounce(200)
  private onSubmitButtonClick() {
    if (this.$refs.form) {
      (this.$refs.form as Form).submitForm();
    }
  }

  private async onSubmit(errors: ValidateError[]): Promise<void> {
    if (await this.checkPreVerifySubmitState(this.verifyScene)) {
      this.$emit('ubtClickSubmit');
      this.$ubtPageButton(
        'finance_submitPlatform',
        'platform',
        this.platformValue,
      );

      if (errors.length) {
        Toast(errors[0].message);
        return;
      }

      if (this.formData && this.submitBtnInLoading === false) {
        this.$ubtPageButton(
          'finance_applyPagePinfo_next',
          '提交数据',
          JSON.stringify(this.formData),
          '用户标签',
          store.state.base.token,
          '返回字典',
          JSON.stringify(this.canSelectContactRelations() || ''),
        );
        this.submitBtnInLoading = true;

        const { userEmergencyContacts } = this.formData;
        try {
          const data: Dictionary = {
            ...this.formData,
            userEmergencyContacts: userEmergencyContacts.map((item, idx) => ({
              ...item,
              contactName: this.getShouldMaskContactName(idx)
                ? this.getOriginContact(idx).defalutContactName
                : trimAll(item.contactName),
              contactPhone: this.getShouldMaskContactPhone(idx)
                ? this.getOriginContact(idx).defalutContactPhone
                : trimAll(item.contactPhone),
            })),
          };

          await UserPersonalInfoApi.updateZYQ({
            ...(data as unknown as UserVo.PersonalInfo),
            updateSource: UpdateZyqSourceEnum.HOME_PAGE,
            updateScene: UpdateZyqSceneEnum.ONLY_CONTACTS,
          });

          // 根据当前场景，与后端交互
          this.$emit('resolveProcessVerify');
        } catch (error) {
          ubtSendError(error as Error);
        } finally {
          this.submitBtnInLoading = false;
        }
      }
    }
  }
}
